<template>
  <div>
    <va-card>
      <div class="categories-tree-table">
        <div
          class="categories-tree-table__head categories-tree-table__grid-row"
        >
          <div class="categories-tree-table__expand-col"></div>
          <div class="categories-tree-table__name-col">Название</div>
          <div class="categories-tree-table__count-col">Кол-во</div>
          <div class="categories-tree-table__title-col">Заголовок</div>
          <div class="categories-tree-table__photo-col">Фото</div>
          <div class="categories-tree-table__description-col">Описание</div>
          <div class="categories-tree-table__hidden-col">Скрыт</div>
          <div class="categories-tree-table__actions-col"></div>
        </div>
        <div class="categories-tree-table__body">
          <category-row
            v-for="(row, index) in flattenedRows"
            :key="row.category.id"
            :category="row.category"
            :level="row.level"
            :expanded-ids="expandedIds"
            :all-categories-count="allCategories.length"
            :index="row.index"
            :siblings="row.siblings"
            @toggle-expand="toggleExpand"
            @add-child="onAddChild"
            @edit="updateHandler"
            @remove="removeHandler"
            @move-up="moveUp"
            @move-down="moveDown"
          />
          <div
            v-if="!flattenedRows.length"
            class="categories-tree-table__empty text-center pa-4"
          >
            Нет категорий
          </div>
        </div>
      </div>
    </va-card>
    <va-modal
      v-model="updateModal"
      hide-default-actions
      no-outside-dismiss
      size="large"
    >
      <category-form
        mode="edit"
        :category="current_category"
        @close="updateModal = false"
      />
    </va-modal>
    <va-modal
      v-model="showCreateModal"
      hide-default-actions
      no-outside-dismiss
      size="large"
    >
      <category-form
        mode="create"
        :parent-id="createParentId"
        :parent-category="createParentCategory"
        @close="closeCreateModal"
      />
    </va-modal>
  </div>
</template>

<script>
import api from "@/api/axios";
import { mapActions, mapState } from "vuex";
import CategoryForm from "./CategoryForm.vue";
import CategoryRow from "./CategoryRow.vue";

export default {
  components: { CategoryForm, CategoryRow },
  props: {
    categories: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      updateModal: false,
      current_category: null,
      showCreateModal: false,
      createParentId: null,
      createParentCategory: null,
      expandedIds: {},
    };
  },
  computed: {
    ...mapState({
      allCategories: (state) => state.categories.categories,
    }),
    categoryTree() {
      return this.buildTree(this.categories, null);
    },
    flattenedRows() {
      const rows = [];
      const walk = (nodes, level, parentSiblings = []) => {
        if (!nodes || !nodes.length) return;
        nodes.forEach((node, index) => {
          rows.push({ 
            category: node, 
            level,
            index,
            siblings: nodes,
          });
          const expanded = this.expandedIds[node.id];
          if (expanded && node.children && node.children.length) {
            walk(node.children, level + 1, node.children);
          }
        });
      };
      walk(this.categoryTree, 0, this.categoryTree);
      return rows;
    },
  },
  methods: {
    ...mapActions({
      fetchCategories: "categories/getCategories",
    }),
    buildTree(cats, parentId) {
      if (!cats || !cats.length) return [];
      return cats
        .filter((c) => (c.parent_id ?? null) === (parentId ?? null))
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map((c) => ({
          ...c,
          children: this.buildTree(cats, c.id),
        }));
    },
    toggleExpand(id) {
      this.$set(this.expandedIds, id, !this.expandedIds[id]);
    },
    onAddChild(category) {
      this.createParentId = category.id;
      this.createParentCategory = category;
      this.showCreateModal = true;
    },
    closeCreateModal() {
      this.showCreateModal = false;
      this.createParentId = null;
      this.createParentCategory = null;
    },
    updateHandler(category) {
      this.updateModal = true;
      this.current_category = category;
    },
    async removeHandler(slug) {
      if (confirm("Вы действительно хотите удалить ?")) {
        try {
          if (this.allCategories.length === 1) {
            alert("Невозможно удалить!");
          } else {
            await api.delete(`/admin/categories/${slug}`);
            this.fetchCategories();
          }
        } catch (error) {
          return error;
        }
      }
    },
    async moveUp(category) {
      await this.reorderCategory(category, -1);
    },
    async moveDown(category) {
      await this.reorderCategory(category, 1);
    },
    async reorderCategory(category, direction) {
      // Находим категорию в дереве и её родителя
      const findCategoryInTree = (nodes, targetId, parent = null) => {
        for (let i = 0; i < nodes.length; i++) {
          if (nodes[i].id === targetId) {
            return { node: nodes[i], index: i, siblings: nodes, parent };
          }
          if (nodes[i].children && nodes[i].children.length) {
            const found = findCategoryInTree(nodes[i].children, targetId, nodes[i]);
            if (found) return found;
          }
        }
        return null;
      };

      const found = findCategoryInTree(this.categoryTree, category.id);
      if (!found) return;

      const { siblings, index } = found;
      const newIndex = index + direction;

      if (newIndex < 0 || newIndex >= siblings.length) return;

      // Меняем местами в массиве
      [siblings[index], siblings[newIndex]] = [siblings[newIndex], siblings[index]];

      // Обновляем порядок на сервере
      await this.updateCategoriesOrder();
    },
    async updateCategoriesOrder() {
      // Собираем все категории с их позициями и parent_id
      const collectCategories = (nodes, parentId = null) => {
        const result = [];
        nodes.forEach((node, index) => {
          result.push({
            id: node.id,
            position: index,
            parent_id: parentId,
          });
          if (node.children && node.children.length) {
            result.push(...collectCategories(node.children, node.id));
          }
        });
        return result;
      };

      const categories = collectCategories(this.categoryTree);

      try {
        await api.post("/admin/categories/update-order", {
          categories: categories,
        });
        await this.fetchCategories();
      } catch (error) {
        console.error("Ошибка обновления порядка категорий:", error);
        alert("Ошибка при сохранении порядка категорий");
        // Перезагружаем категории для восстановления правильного порядка
        await this.fetchCategories();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.categories-tree-table {
  width: 100%;

  &__grid-row {
    display: grid;
    grid-template-columns: 48px 1fr 70px 1fr 80px 1fr 70px 140px;
    gap: 0 16px;
    align-items: center;
    padding: 0 16px;
  }

  &__head {
    min-height: 48px;
    padding: 12px 16px;
    font-weight: 600;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    background: rgba(0, 0, 0, 0.02);
  }

  &__count-col {
    text-align: center;
  }
  &__empty {
    grid-column: 1 / -1;
    padding: 16px;
    color: #6c757d;
  }
}
.text-center {
  text-align: center;
}
.pa-4 {
  padding: 16px;
}
@import "src/assets/scss/components/tooltip.scss";
</style>

<style lang="scss" scoped>
.success,
.warning,
.danger,
.primary {
  border-radius: 11px;
  padding: 5px;
}
.success {
  background-color: #4bb543;
  color: #fff;
}
.warning {
  background-color: #f2f236;
  color: #000;
}
.danger {
  background-color: #df4759;
  color: #fff;
}
.primary {
  background-color: #0275d8;
  color: rgb(255, 255, 255);
}
</style>
